import { useRef, useState } from "react";
import { UploadCloud, Loader2, X, Star, GripVertical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Multi-image uploader with main-image selection and drag-to-reorder.
 *
 * Props:
 *  - value: string[]   — current list of image URLs (first = main)
 *  - onChange: (string[]) => void
 *  - max: number (default 6)
 */
export const MultiImageUpload = ({ value = [], onChange, max = 6, testid = "multi-image-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const dragIndex = useRef(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${max} images.`);
      return;
    }
    const slice = arr.slice(0, remaining);
    setUploading(true);
    const next = [...value];
    for (const f of slice) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} n'est pas une image.`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data.url);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Échec upload ${f.name}`);
      }
    }
    onChange(next);
    setUploading(false);
    if (slice.length === 1) toast.success("Image ajoutée");
    else if (slice.length > 1) toast.success(`${slice.length} images ajoutées`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  const removeAt = (idx) => {
    const url = value[idx];
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
    if (url) api.post("/admin/files/delete", { url }).catch(() => {});
  };

  const makeMain = (idx) => {
    if (idx === 0) return;
    const next = [...value];
    const [m] = next.splice(idx, 1);
    next.unshift(m);
    onChange(next);
  };

  const onDragStart = (idx) => (e) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const onDropOnItem = (idx) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === idx) return;
    const next = [...value];
    const [m] = next.splice(from, 1);
    next.splice(idx, 0, m);
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      {/* Existing images grid */}
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {value.map((url, idx) => (
            <div
              key={url + idx}
              draggable
              onDragStart={onDragStart(idx)}
              onDragOver={onDragOver(idx)}
              onDrop={onDropOnItem(idx)}
              data-testid={`${testid}-item-${idx}`}
              className={`relative aspect-square bg-sand border rounded-sm overflow-hidden group cursor-move ${
                idx === 0 ? "border-copper ring-1 ring-copper" : "border-stone"
              }`}
            >
              <img src={url} alt="" className="w-full h-full object-cover" />
              {idx === 0 && (
                <span className="absolute top-1 left-1 bg-copper text-cream text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 flex items-center gap-1">
                  <Star className="h-2.5 w-2.5 fill-current" /> Principale
                </span>
              )}
              <div className="absolute inset-0 bg-ink/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5">
                {idx !== 0 && (
                  <button
                    type="button"
                    onClick={() => makeMain(idx)}
                    data-testid={`${testid}-make-main-${idx}`}
                    className="bg-cream text-ink p-1.5 rounded-sm hover:bg-copper hover:text-cream"
                    title="Définir comme principale"
                  >
                    <Star className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  data-testid={`${testid}-remove-${idx}`}
                  className="bg-cream text-destructive p-1.5 rounded-sm hover:bg-destructive hover:text-cream"
                  title="Retirer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <GripVertical className="absolute bottom-1 right-1 h-3 w-3 text-cream/70 opacity-0 group-hover:opacity-100" />
            </div>
          ))}
        </div>
      )}

      {/* Drop / pick zone */}
      {value.length < max && (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`cursor-pointer border-2 border-dashed rounded-sm flex items-center gap-3 p-4 transition-colors ${
            drag ? "border-gold bg-gold/5" : "border-stone hover:border-ink"
          }`}
          data-testid={`${testid}-dropzone`}
        >
          <div className="h-12 w-12 bg-sand rounded-sm flex items-center justify-center shrink-0">
            {uploading ? (
              <Loader2 className="h-5 w-5 text-gold animate-spin" />
            ) : (
              <UploadCloud className="h-5 w-5 text-ash" strokeWidth={1.4} />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-ink">
              {uploading
                ? "Téléversement…"
                : value.length === 0
                  ? "Cliquez ou glissez vos images (la 1ère sera l'image principale)"
                  : `Ajouter d'autres images (${value.length}/${max})`}
            </p>
            <p className="text-xs text-ash mt-0.5">JPG, PNG, WEBP — max 8 Mo par image</p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            data-testid={`${testid}-input`}
            className="hidden"
            onChange={(e) => upload(e.target.files)}
          />
        </div>
      )}
    </div>
  );
};
